/**
 * Adjustable table columns (owner 2026-09-27: "make the columns adjustable with a table template
 * … default it same as tradovate defaults"). The pure model behind every HELM grid: a column
 * registry per table, the per-panel column state (which columns show, in what order, how wide,
 * sorted how), sorting and subtotals. Rendering lives in `apps/web/src/tables/`; the registries
 * (value getters) sit next to this file so their exact integer math is unit-tested.
 *
 * Everything here is pure and DOM-free; nothing touches the order path.
 */

/** An exact price in ticks as a reduced fraction (`openCostTicks / |netQty|`). */
export type Rational = { num: bigint; den: bigint };

/** What a cell holds before formatting. null = unknown (rendered `$ --` / `—`). */
export type CellValue = bigint | number | string | Rational | null;

/**
 * How the web layer formats a cell:
 * - `money`: unsigned cents (balances, fees) · `pnl`: signed cents, green/red/grey
 * - `price`: integer ticks in the row's instrument format · `avgPrice`: a `Rational` in ticks
 * - `qty` / `signedQty` / `int`: integers · `ticksPerContract`: a `Rational` of ticks
 * - `time`: epoch ms · `duration`: ms · `tickValue`: cents per tick
 * - `text`, `account`, `symbol`, `side`, `status`: strings; `action`: a (disabled) row button
 */
export type CellKind =
  | "text"
  | "account"
  | "symbol"
  | "side"
  | "status"
  | "qty"
  | "signedQty"
  | "int"
  | "money"
  | "pnl"
  | "price"
  | "avgPrice"
  | "ticksPerContract"
  | "time"
  | "duration"
  | "tickValue"
  | "action";

export type ColumnDef<R> = {
  /** Stable id stored in panel params and templates; never reuse an id for another meaning. */
  id: string;
  /** Header label. */
  label: string;
  /** Picker group ("Position", "P&L", "Risk", …). */
  category: string;
  /** Precise definition, shown as the header tooltip and in the picker. */
  definition: string;
  kind: CellKind;
  /** Default and minimum width in px. */
  width: number;
  minWidth: number;
  /** Cannot be hidden (Tradovate locks Instrument / Account). */
  locked?: boolean;
  /** Summed in per-account subtotal and total rows (money columns only). */
  subtotal?: boolean;
  /** Not sortable (row actions). */
  unsortable?: boolean;
  /**
   * The value is not available from HELM's feeds yet: which feed fills it. The column still shows
   * (`$ --` / `—`) with this in its tooltip.
   */
  pending?: string;
  /** Unknown values show "—" rather than "$ --". */
  dash?: boolean;
  /**
   * Per-row tooltip: where the value came from, or which setting is missing when it is unknown
   * (owner 2026-09-29: "—" with a tooltip naming the missing setting).
   */
  rowTitle?: (row: R) => string | null;
  /** Per-row warning colour (SPEC-ADDENDA A3: 75 % / 90 % of a limit consumed). */
  level?: (row: R) => "warn" | "danger" | null;
  /**
   * Per-row source badge of a room figure: a Rithmic-derived one (ADR-0045) is "unverified" until
   * the owner's R|Trader Pro match, then "verified"; "helm" marks a figure HELM calculated from
   * the owner's firm rules (ADR-0047). Shown next to the value, never instead of it.
   */
  badge?: (row: R) => "unverified" | "verified" | "helm" | null;
  value: (row: R) => CellValue;
};

export type TableId = "positions" | "accounts" | "orders" | "fills";

export type TableDef<R> = {
  id: TableId;
  label: string;
  columns: readonly ColumnDef<R>[];
  /** Default visible columns in order (Tradovate's defaults, docs/DESIGN-REFERENCE.md). */
  defaults: readonly string[];
  /** Column inserted after the first one when several accounts show and it is hidden. */
  autoAccountColumn?: string;
  /** Read-only templates shipped with HELM (e.g. each platform's defaults); ids start "builtin:". */
  builtIns?: readonly BuiltInTemplate[];
  /** The built-in template the defaults are (shown as "Template: X" on a default table). */
  defaultTemplate?: string;
  /**
   * Retired column id → the column that replaces it. Saved and synced column states that still
   * list the old id show the replacement in the same place (unless it is already shown).
   */
  replacedColumns?: Readonly<Record<string, string>>;
};

/** A shipped, read-only column set. */
export type BuiltInTemplate = { id: string; name: string; columns: readonly string[] };

export type SortDir = "asc" | "desc";
export type SortSpec = { id: string; dir: SortDir };

export const COLUMN_STATE_VERSION = 1;
export const MAX_COLUMN_WIDTH = 800;

/** One table instance's columns, persisted in the panel's params (`WidgetParams.tables`). */
export type ColumnState = {
  v: typeof COLUMN_STATE_VERSION;
  /** Visible column ids, in display order. */
  columns: string[];
  /** Widths the user dragged (px); absent = the column's default. */
  widths: Record<string, number>;
  sort: SortSpec | null;
  /** Template last applied (id), for the picker's "Template: X" line; null = none. */
  template: string | null;
};

export function columnById<R>(def: TableDef<R>, id: string): ColumnDef<R> | undefined {
  return def.columns.find((c) => c.id === id);
}

export function defaultColumnState<R>(def: TableDef<R>): ColumnState {
  return {
    v: COLUMN_STATE_VERSION,
    columns: [...def.defaults],
    widths: {},
    sort: null,
    template: def.defaultTemplate ?? null,
  };
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

export function clampWidth<R>(col: ColumnDef<R>, w: number): number {
  if (!Number.isFinite(w)) return col.width;
  return Math.max(col.minWidth, Math.min(MAX_COLUMN_WIDTH, Math.round(w)));
}

/** Keep only widths of known columns, clamped; drop entries equal to the default. */
function cleanWidths<R>(def: TableDef<R>, raw: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (!isRecord(raw)) return out;
  for (const [id, w] of Object.entries(raw)) {
    const col = columnById(def, id);
    if (!col || typeof w !== "number") continue;
    const c = clampWidth(col, w);
    if (c !== col.width) out[id] = c;
  }
  return out;
}

/** Known, de-duplicated ids with every locked column present (locked ones keep registry order up front). */
function cleanColumnList<R>(def: TableDef<R>, raw: readonly unknown[]): string[] {
  const seen = new Set<string>();
  const list: string[] = [];
  for (const rawId of raw) {
    const id = typeof rawId === "string" ? (def.replacedColumns?.[rawId] ?? rawId) : rawId;
    if (typeof id !== "string" || seen.has(id) || !columnById(def, id)) continue;
    seen.add(id);
    list.push(id);
  }
  const missingLocked = def.columns.filter((c) => c.locked === true && !seen.has(c.id)).map((c) => c.id);
  return [...missingLocked, ...list];
}

/**
 * Validate stored or synced column state (migration included): unknown ids (a column removed in
 * a later version) are dropped, locked columns are restored, widths clamped to [min, 800], a sort
 * on an unknown or unsortable column is cleared. Anything unusable → the table's defaults. Layouts
 * saved before column state existed (no `tables` in the params) also land here as `undefined`.
 */
export function sanitizeColumnState<R>(raw: unknown, def: TableDef<R>): ColumnState {
  if (!isRecord(raw) || !Array.isArray(raw["columns"])) return defaultColumnState(def);
  const columns = cleanColumnList(def, raw["columns"] as unknown[]);
  if (columns.length === 0) return defaultColumnState(def);
  let sort: SortSpec | null = null;
  const s = raw["sort"];
  if (isRecord(s) && typeof s["id"] === "string" && (s["dir"] === "asc" || s["dir"] === "desc")) {
    const col = columnById(def, s["id"]);
    if (col && col.unsortable !== true) sort = { id: s["id"], dir: s["dir"] };
  }
  return {
    v: COLUMN_STATE_VERSION,
    columns,
    widths: cleanWidths(def, raw["widths"]),
    sort,
    template: typeof raw["template"] === "string" && raw["template"] !== "" ? raw["template"] : null,
  };
}

/** Show or hide a column (shown columns are appended at the end, like Tradovate's picker). */
export function setColumnVisible<R>(
  s: ColumnState,
  def: TableDef<R>,
  id: string,
  visible: boolean,
): ColumnState {
  const col = columnById(def, id);
  if (!col) return s;
  const has = s.columns.includes(id);
  if (visible === has) return s;
  if (!visible) {
    if (col.locked === true || s.columns.length <= 1) return s;
    return {
      ...s,
      columns: s.columns.filter((c) => c !== id),
      sort: s.sort?.id === id ? null : s.sort,
    };
  }
  return { ...s, columns: [...s.columns, id] };
}

/** Move a visible column to `toIndex` (clamped). */
export function moveColumn(s: ColumnState, id: string, toIndex: number): ColumnState {
  const i = s.columns.indexOf(id);
  if (i < 0) return s;
  const list = [...s.columns];
  list.splice(i, 1);
  const to = Math.max(0, Math.min(list.length, Math.trunc(toIndex)));
  if (to === i) return s;
  list.splice(to, 0, id);
  return { ...s, columns: list };
}

/** Keyboard reorder: one step left (−1) or right (+1). */
export function moveColumnBy(s: ColumnState, id: string, delta: number): ColumnState {
  const i = s.columns.indexOf(id);
  return i < 0 ? s : moveColumn(s, id, i + delta);
}

/** Drag-and-drop: put `id` where `targetId` is (before it when moving left, after when moving right). */
export function moveColumnTo(s: ColumnState, id: string, targetId: string): ColumnState {
  const to = s.columns.indexOf(targetId);
  return to < 0 || id === targetId ? s : moveColumn(s, id, to);
}

export function columnWidth<R>(s: ColumnState, col: ColumnDef<R>): number {
  return s.widths[col.id] ?? col.width;
}

/** Resize, clamped to the column's minimum and 800 px. */
export function resizeColumn<R>(s: ColumnState, def: TableDef<R>, id: string, width: number): ColumnState {
  const col = columnById(def, id);
  if (!col) return s;
  const w = clampWidth(col, width);
  if (columnWidth(s, col) === w) return s;
  const others = Object.entries(s.widths).filter(([k]) => k !== id);
  const widths = Object.fromEntries(w === col.width ? others : [...others, [id, w]]) as Record<
    string,
    number
  >;
  return { ...s, widths };
}

/** Header click: unsorted → ascending → descending → unsorted. */
export function cycleSort<R>(s: ColumnState, def: TableDef<R>, id: string): ColumnState {
  const col = columnById(def, id);
  if (!col || col.unsortable === true) return s;
  if (s.sort?.id !== id) return { ...s, sort: { id, dir: "asc" } };
  if (s.sort.dir === "asc") return { ...s, sort: { id, dir: "desc" } };
  return { ...s, sort: null };
}

/**
 * The columns to render: the state's visible columns, plus the Account column after the first one
 * when several accounts show and the user has not placed it themselves (Q-W1 behaviour).
 */
export function visibleColumns<R>(
  s: ColumnState,
  def: TableDef<R>,
  opts: { multiAccount: boolean },
): ColumnDef<R>[] {
  const ids = [...s.columns];
  const auto = def.autoAccountColumn;
  if (opts.multiAccount && auto !== undefined && !ids.includes(auto)) ids.splice(1, 0, auto);
  return ids.map((id) => columnById(def, id)).filter((c): c is ColumnDef<R> => c !== undefined);
}

/** True when the state shows exactly the defaults, at default widths and unsorted. */
export function isDefaultColumnState<R>(s: ColumnState, def: TableDef<R>): boolean {
  return (
    s.sort === null &&
    Object.keys(s.widths).length === 0 &&
    s.columns.length === def.defaults.length &&
    s.columns.every((c, i) => c === def.defaults[i])
  );
}

// ---------------------------------------------------------------------------------------------
// Sorting

function isRational(v: CellValue): v is Rational {
  return typeof v === "object" && v !== null;
}

/**
 * Total order over cell values of one column: numbers exactly (bigint and Rational by cross
 * multiplication, never a float), strings case-insensitively with numeric collation. Unknown
 * (null) always sorts last, whatever the direction.
 */
export function compareCells(a: CellValue, b: CellValue): number {
  if (a === null || b === null) return a === b ? 0 : a === null ? 1 : -1;
  if (typeof a === "string" && typeof b === "string")
    return a.localeCompare(b, "en", { numeric: true, sensitivity: "base" });
  // A column holds one kind of value; mixed, numbers sort before text.
  if (typeof a === "string") return 1;
  if (typeof b === "string") return -1;
  const [an, ad] = isRational(a) ? [a.num, a.den] : [typeof a === "bigint" ? a : null, 1n];
  const [bn, bd] = isRational(b) ? [b.num, b.den] : [typeof b === "bigint" ? b : null, 1n];
  if (an !== null && bn !== null) {
    // Denominators are positive (reduced |qty|).
    const l = an * bd;
    const r = bn * ad;
    return l < r ? -1 : l > r ? 1 : 0;
  }
  const x = typeof a === "number" ? a : Number(an) / Number(ad);
  const y = typeof b === "number" ? b : Number(bn) / Number(bd);
  return x < y ? -1 : x > y ? 1 : 0;
}

/** Stable sort by a column; unknown values last. Returns the input when unsorted. */
export function sortRows<R>(rows: readonly R[], def: TableDef<R>, sort: SortSpec | null): readonly R[] {
  if (sort === null) return rows;
  const col = columnById(def, sort.id);
  if (!col || col.unsortable === true) return rows;
  const sign = sort.dir === "asc" ? 1 : -1;
  return rows
    .map((r, i) => ({ r, i, v: col.value(r) }))
    .sort((x, y) => {
      if (x.v === null || y.v === null) {
        const c = compareCells(x.v, y.v);
        return c !== 0 ? c : x.i - y.i;
      }
      return sign * compareCells(x.v, y.v) || x.i - y.i;
    })
    .map((x) => x.r);
}

// ---------------------------------------------------------------------------------------------
// Subtotals

/**
 * Σ of each subtotal column over `rows` (exact cents; null when any row's value is unknown).
 * Only columns flagged `subtotal` (money) are summed; others are absent from the map.
 */
export function subtotals<R>(rows: readonly R[], cols: readonly ColumnDef<R>[]): Map<string, bigint | null> {
  const out = new Map<string, bigint | null>();
  for (const c of cols) {
    if (c.subtotal !== true) continue;
    let t: bigint | null = rows.length === 0 ? null : 0n;
    for (const r of rows) {
      const v = c.value(r);
      if (typeof v !== "bigint" || t === null) {
        t = null;
        break;
      }
      t += v;
    }
    out.set(c.id, t);
  }
  return out;
}
